import { FileAddOutlined, LinkOutlined } from "@ant-design/icons"
import {
    LINE_ITEM_LINK_MAX_LENGTH,
    LINE_ITEM_LINK_MIN_LENGTH,
    LINE_ITEM_MATERIAL_CODE_MAX_LENGTH,
    LINE_ITEM_MATERIAL_CODE_MIN_LENGTH,
    LINE_ITEM_NAME_MAX_LENGTH,
    LINE_ITEM_NAME_MIN_LENGTH,
    LINE_ITEM_NOTE_MAX_LENGTH,
    LINE_ITEM_NOTE_MIN_LENGTH,
    LINE_ITEM_QUANTITY_MAX,
    LINE_ITEM_QUANTITY_MIN_BIGGER_THAN,
} from "@finway-group/shared/lib/models/purchaseOrder/lineItem.validation"
import { UNIT_PRICE_PRECISION } from "@finway-group/shared/lib/utils/price.calculator.utils"
import { Button, Col, Collapse, Form, Input, InputNumber, Row } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { CurrencyInput } from "~Components/currencyInput"
import { CostCenter2Select } from "~Components/form/CostCenter2Select"
import { CostCenterSelect } from "~Components/form/CostCenterSelect"
import { ExpenseAccountSelect } from "~Components/form/ExpenseAccountSelect"
import { FormItemCurrency } from "~Components/form/formItemCurrency"
import FormTaxRateSelector from "~Components/form/formTaxRateSelector"
import { UnitOfMeasureSelect } from "~Components/form/UnitOfMeasureSelect"
import { costCenterRules } from "~Components/forms/rules/shared.rules"
import { ExpandableListItemIcon } from "~Components/icons/ExpandableListItemIcon"
import { TrashIcon } from "~Components/icons/TrashIcon"
import { createLineItemRules } from "~Features/pages/purchaseOrders/components/LineItem.rules"
import { useCompany } from "~Shared/hooks/company.hooks"
import { useSubCostCenters } from "~Shared/hooks/costCenter.hooks"
import { useCostCenters2 } from "~Shared/hooks/costCenter2.hooks"
import { useExpenseAccounts } from "~Shared/hooks/expenseAccount.hooks"
import { useTaxes } from "~Shared/hooks/tax.hooks"
import { getDefaultTaxId } from "~Shared/utils/helper.utils"
import { getPopupAnchor } from "~Shared/utils/popup.utils"
import { type LineItemCardProps } from "../types/LineItems.interfaces"

const ExpandIcon = ({ isActive }: { isActive?: boolean }) => <ExpandableListItemIcon className="mt-5" expanded={isActive ?? false} />

export const LineItemCard = ({ field, idx, expanded, onRemove, onChange, canRemove, currency, onToggleExpand }: LineItemCardProps) => {
    const { t } = useTranslation()
    const lineItemRules = createLineItemRules()

    const taxValues = useTaxes()
    const costCenters = useSubCostCenters(true)
    const costCenters2 = useCostCenters2()
    const expenseAccounts = useExpenseAccounts()
    const { showCostCentersIds } = useCompany()

    const getColumnSpan = () => {
        const hasExpenseAccounts = expenseAccounts.length > 0
        const hasCostCenters2 = costCenters2.length > 0

        if (hasExpenseAccounts && hasCostCenters2) {
            return 6
        }

        if (hasExpenseAccounts || hasCostCenters2) {
            return 9
        }

        return 18
    }

    const firstRow = (
        <>
            {/* Row 1: #, Material code, Description, Arrow, X */}
            <Row
                gutter={12}
                align="top"
                onClick={(e) => {
                    e.stopPropagation()
                }}
            >
                <Col span={4}>
                    <Form.Item>
                        <Input className="border-0 text-center material-code-input rounded-full" value={(idx + 1).toString().padStart(2, "0")} disabled readOnly />
                    </Form.Item>
                </Col>

                <Col span={6}>
                    <Form.Item name={[field.name, "materialCode"]} rules={lineItemRules.materialCode}>
                        <Input
                            className="border-0 shadow-none outline-none rounded-full align-middle text-center h-34 material-code-input"
                            placeholder={t("placeholder:request.material_code")}
                            onChange={(e) => onChange(idx, e.target.value, "materialCode")}
                            minLength={LINE_ITEM_MATERIAL_CODE_MIN_LENGTH}
                            maxLength={LINE_ITEM_MATERIAL_CODE_MAX_LENGTH}
                        />
                    </Form.Item>
                </Col>
                <Col span={canRemove ? 12 : 14}>
                    <Form.Item name={[field.name, "name"]} rules={lineItemRules.name}>
                        <Input
                            prefix={<span style={{ color: "#FF0000" }}>*</span>}
                            placeholder={t("input:request.item_name")}
                            className="w-full border-none m-0 p-0"
                            onChange={(e) => onChange(idx, e.target.value, "name")}
                            minLength={LINE_ITEM_NAME_MIN_LENGTH}
                            maxLength={LINE_ITEM_NAME_MAX_LENGTH}
                        />
                    </Form.Item>
                </Col>
                <Col span={2}>
                    {canRemove ? (
                        <Form.Item>
                            <Button
                                type="text"
                                shape="circle"
                                icon={<TrashIcon />}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onRemove(idx)
                                }}
                                className="cursor-pointer"
                                aria-label="Remove line item"
                            />
                        </Form.Item>
                    ) : null}
                </Col>
            </Row>
        </>
    )

    const secondRow = (
        <>
            {/* Row 2: Qty, UoM, Unit price, VAT% */}
            <Row gutter={12} align="top">
                <Col span={4}>
                    <Form.Item label={t("purchaseOrders:drawer.quantity")} name={[field.name, "quantity"]} rules={lineItemRules.quantity}>
                        <InputNumber
                            placeholder="0"
                            size="small"
                            type="number"
                            min={LINE_ITEM_QUANTITY_MIN_BIGGER_THAN}
                            max={LINE_ITEM_QUANTITY_MAX}
                            onChange={(value) => {
                                onChange(idx, value || 0, "quantity")
                            }}
                        />
                    </Form.Item>
                </Col>
                <Col span={4}>
                    <Form.Item label={t("input:request.unit_of_measure")} name={[field.name, "unitOfMeasure"]} rules={lineItemRules.unitOfMeasure}>
                        <UnitOfMeasureSelect onChange={(value) => onChange(idx, value, "unitOfMeasure")} />
                    </Form.Item>
                </Col>
                <Col span={4}>
                    <FormItemCurrency currency={currency} name={[field.name, "unitPrice"]} label={t("input:request.unit_price")} rules={lineItemRules.genericPrice}>
                        <CurrencyInput currency={currency} precision={UNIT_PRICE_PRECISION} value={0} onChange={(value) => onChange(idx, value, "unitPrice")} />
                    </FormItemCurrency>
                </Col>

                <FormTaxRateSelector
                    data-testid="lineItemtax"
                    elem={{
                        name: [field.name, "taxRate", "_id"],
                        label: t("input:request.tax_rate"),
                        onChange: (value) => onChange(idx, value as string, "taxRate"),
                        key: "taxRate",
                        value: getDefaultTaxId(taxValues),
                        isRequired: false,
                        isDisabled: false,
                        isBordered: false,
                        className: "text-xs md:text-base no-padding-select border-none",
                        getPopupAnchor: getPopupAnchor(),
                        taxValues,
                    }}
                    span={4}
                />

                <Col span={4}>
                    <FormItemCurrency currency={currency} name={[field.name, "totalNetPrice"]} label={t("input:request.net_price")} rules={lineItemRules.genericPrice}>
                        <CurrencyInput currency={currency} value={0} onChange={(value) => onChange(idx, value, "totalNetPrice")} />
                    </FormItemCurrency>
                </Col>
                <Col span={4}>
                    <FormItemCurrency currency={currency} name={[field.name, "totalGrossPrice"]} label={t("input:request.gross_price")} rules={lineItemRules.genericPrice}>
                        <CurrencyInput currency={currency} value={0} onChange={(value) => onChange(idx, value, "totalGrossPrice")} />
                    </FormItemCurrency>
                </Col>
            </Row>
        </>
    )

    return (
        <>
            <Collapse ghost expandIcon={ExpandIcon} activeKey={expanded ? [idx] : []} onChange={() => onToggleExpand(idx)}>
                <Collapse.Panel key={idx} header={firstRow} className="w-full" forceRender>
                    {secondRow}
                    {/* Row 4: Cost center, Cost unit, Exp. Account */}
                    <Row gutter={12} align="bottom">
                        <Col span={6}>
                            <FormItemCurrency currency={currency} name={[field.name, "totalTaxPrice"]} label={t("input:request.tax_price")} rules={lineItemRules.taxPrice}>
                                <CurrencyInput currency={currency} value={0} onChange={(value) => onChange(idx, value, "totalTaxPrice")} />
                            </FormItemCurrency>
                        </Col>

                        <Col span={getColumnSpan()}>
                            <Form.Item label={t("input:request.cost_center")} name={[field.name, "costCenter"]} required rules={costCenterRules()}>
                                <CostCenterSelect
                                    costCenters={costCenters}
                                    showCostCentersIds={showCostCentersIds}
                                    getPopupAnchor={getPopupAnchor}
                                    onSelect={(value) => onChange(idx, value, "costCenter")}
                                    placeholder={t("placeholder:cost_center.select_a_cost_center")}
                                    notFoundContent={t("missing:no_cost_center")}
                                />
                            </Form.Item>
                        </Col>

                        <Col span={costCenters2.length > 0 ? getColumnSpan() : 0}>
                            {costCenters2.length > 0 ? (
                                <Form.Item label={t("purchaseOrders:drawer.costCenter2")} name={[field.name, "costCenter2"]}>
                                    <CostCenter2Select
                                        costCenters2={costCenters2}
                                        getPopupAnchor={getPopupAnchor}
                                        onSelect={(value) => onChange(idx, value, "costCenter2")}
                                        placeholder={t("placeholder:cost_center_2.select_a_cost_center_2")}
                                        notFoundContent={t("missing:no_cost_center_2")}
                                    />
                                </Form.Item>
                            ) : null}
                        </Col>

                        <Col span={expenseAccounts.length > 0 ? getColumnSpan() : 0}>
                            {expenseAccounts.length > 0 ? (
                                <Form.Item label={t("input:request.expense_account")} name={[field.name, "expenseAccount"]}>
                                    <ExpenseAccountSelect
                                        expenseAccounts={expenseAccounts}
                                        onSelect={(value) => onChange(idx, value, "expenseAccount")}
                                        placeholder={t("placeholder:expense_account.select_an_expense_account")}
                                    />
                                </Form.Item>
                            ) : null}
                        </Col>
                    </Row>

                    {/* Row 5: Link, Note */}
                    <Row gutter={12} align="bottom">
                        <Col span={12}>
                            <Form.Item label={t("purchaseOrders:drawer.link")} name={[field.name, "link"]} rules={lineItemRules.link}>
                                <Input
                                    prefix={<LinkOutlined />}
                                    placeholder={t("purchaseOrders:drawer.addUrlLink")}
                                    className="border-none"
                                    onChange={(e) => onChange(idx, e.target.value, "link")}
                                    minLength={LINE_ITEM_LINK_MIN_LENGTH}
                                    maxLength={LINE_ITEM_LINK_MAX_LENGTH}
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item label={t("purchaseOrders:drawer.note")} name={[field.name, "note"]} rules={lineItemRules.note}>
                                <Input
                                    prefix={<FileAddOutlined />}
                                    placeholder={t("purchaseOrders:drawer.addNote")}
                                    className="border-none"
                                    onChange={(e) => onChange(idx, e.target.value, "note")}
                                    minLength={LINE_ITEM_NOTE_MIN_LENGTH}
                                    maxLength={LINE_ITEM_NOTE_MAX_LENGTH}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Collapse.Panel>
            </Collapse>
        </>
    )
}
